// How much of the body to look at when classifying it. Never reported — see below.
const HEAD_LENGTH = 64

// Cap on the reported key names, so a pathological body can't blow up the payload.
const MAX_KEYS = 20

// Classifies a string body by SHAPE ONLY. Reporting a prefix of the raw bytes would not be safe:
// a truncated response of ours also fails JSON.parse and therefore arrives here as a string, so
// those bytes can hold the user object and its uploadToken — and truncation (a torn-down
// connection, an edge proxy losing the origin) is one of the very causes this is meant to identify.
const classifyStringBody = (body) => {
  const head = body.slice(0, HEAD_LENGTH)
    .trim()
    .toLowerCase()

  if (!head) {
    return body.length === 0 ? 'empty' : 'whitespace'
  }

  // A preflight answered with res.sendStatus(200) writes "OK" as text/plain, on the very same URL
  // as the GET it clears — so a method-blind cache can serve it for the GET. allow-cors.js now
  // answers 204 with no body instead, which lands in `empty` above; the tell for a replayed
  // preflight is therefore the httpStatus (204 on a GET) rather than this classification. Kept for
  // app versions built before that change, and for any other hop that answers a GET with "OK".
  if (head === 'ok') {
    return 'preflight-ok'
  }

  // captive portal / AV middlebox interstitial
  if (head.startsWith('<')) {
    return 'markup'
  }

  // JSON that failed to parse: either one of our payloads cut short, or a proxy's own error body
  if (head.startsWith('{') || head.startsWith('[')) {
    return 'json-like'
  }

  return 'other'
}

// Describes an unexpected axios response body for raygun WITHOUT reporting any of its content.
//
// axios' default transformResponse leaves `data` as a string only when JSON.parse failed on it (or
// the body was empty), and as an object when it parsed. Neither form may be reported verbatim: the
// string form can be a truncated payload of ours, and the object form can be a payload of ours with
// an unexpected root key. So a string is reduced to a shape classification and a length, and an
// object to its top-level key NAMES — never its values.
export default (data) => {
  const type = data === null ? 'null' : typeof data

  if (type === 'string') {
    return {
      type,
      length: data.length,
      classification: classifyStringBody(data),
    }
  }

  if (type === 'object') {
    const keys = Object.keys(data)

    return {
      type,
      keyCount: keys.length,
      keys: keys.slice(0, MAX_KEYS),
    }
  }

  return { type }
}
